import React, {memo} from 'react';
import {useSelector} from 'react-redux';
import {RouteName} from '@/app/constants/routing';
import {AnyRouteResult} from '@/app/interfaces/routing';
import {WeatherForecastLocation} from '@/app/interfaces/weather';
import {useLocalizer} from "@/common/services/localizer";
import {useRoute} from '@/common/services/router';
import {useGetPageSeoMeta} from "@/app/hooks/useGetPageSeoMeta";
import {useUrlBuilder} from '@/app/routing/useUrlBuilder';
import {selectDomainConfig} from '@/app/store/config/selectors';
import logo from './icons/logo.svg?file';

interface WeatherLocationSchemaMarkupProps {
	location: WeatherForecastLocation;
}

// Return ISO string with local timezone, e.g. 2025-10-23T14:30:00+03:00
function toISOWithTimezone(date: Date) {
	const offset = -date.getTimezoneOffset();
	const sign = offset >= 0 ? '+' : '-';
	const hours = String(Math.trunc(Math.abs(offset) / 60)).padStart(2, '0');
	const minutes = String(Math.abs(offset) % 60).padStart(2, '0');
	const localDate = new Date(date.getTime() + offset * 60000);
	return `${localDate.toISOString().slice(0, -1)}${sign}${hours}:${minutes}`;
}

const langMap: Record<string, string> = {
	uk: 'uk-UA',
	en: 'en-US',
	pl: 'pl-PL',
	ru: 'ru-RU',
};

export const WeatherLocationSchemaMarkup = memo<WeatherLocationSchemaMarkupProps>(props => {
	const {location} = props;

	const t = useLocalizer();
	const {domainName} = useSelector(selectDomainConfig);
	const {route, urlGenerator} = useRoute<AnyRouteResult>();
	const urlBuilder = useUrlBuilder();
	const pageSeoMeta = useGetPageSeoMeta();

	const urlPath =
		route.name === RouteName.mainPage
			? urlBuilder(RouteName.weather, {location})
			: urlGenerator(route.name, route.params);

	const url = `https://${domainName}${urlPath}`.replace(/\/+$/, '');
	const now = new Date();
	// Jan 1 of last year
	const datePublished = toISOWithTimezone(new Date(new Date().getFullYear() - 1, 0, 1));
	// Current hour rounded to 0 or 30 min
	const dateModified = toISOWithTimezone(new Date(now.setMinutes(now.getMinutes() < 30 ? 0 : 30, 0, 0)));

	return (
		<script
			dangerouslySetInnerHTML={{
				__html: JSON.stringify({
					'@context': 'https://schema.org',
					'@type': 'WebPage',
					name: pageSeoMeta.title,
					description: pageSeoMeta.description,
					url,
					datePublished,
					dateModified,
					inLanguage: langMap[t.language] || 'uk-UA',
					mainEntity: {
						'@type': 'Place',
						name: location.title,
						geo: {
							'@type': 'GeoCoordinates',
							latitude: location.coordinates.lat.toFixed(4),
							longitude: location.coordinates.lng.toFixed(4),
						},
						address: {
							'@type': 'PostalAddress',
							addressLocality: location.title,
							addressRegion: location.superior.region?.title,
							addressCountry: location.superior.country?.alpha2Code,
						},
					},
					about: {
						'@type': 'Place',
						name: location.title,
						geo: {
							'@type': 'GeoCoordinates',
							latitude: location.coordinates.lat.toFixed(4),
							longitude: location.coordinates.lng.toFixed(4),
						},
					},
					publisher: {
						'@type': 'Organization',
						name: domainName.charAt(0).toUpperCase() + domainName.slice(1),
						url: `https://${domainName}`,
						logo: {
							'@type': 'ImageObject',
							url: `https://${domainName}${logo}`,
						},
					},
				}),
			}}
			type='application/ld+json'
		/>
	);
});

WeatherLocationSchemaMarkup.displayName = 'WeatherLocationSchemaMarkup';
